// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import VideoList from './components/VideoList';
import './App.css';
import PropTypes from 'prop-types';

// const fetchVideosAPI = process.env.REACT_APP_FETCH_VIDEOS_API;
// const downloadVideosAPI = process.env.REACT_APP_DOWNLOAD_VIDEOS_API;

function App({ keycloak, isAuthenticated }) {

  //////////////////////////// API ENDPOINTS ////////////////////////////////////////

  const downloadVideosAPI = process.env.REACT_APP_DOWNLOAD_API;
  const fetchVideosAPI = process.env.REACT_APP_FETCH_API;

  //////////////////////////////////////////////////////////////////////////////////


  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingError, setProcessingError] = useState(null);



  // Cargar videos
  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {};
      if (isAuthenticated && keycloak.token) {
        headers['Authorization'] = `Bearer ${keycloak.token}`;
      }

      const response = await fetch(fetchVideosAPI, { headers, cache: 'no-store' });

      if (!response?.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      setVideos(data);
      setLoading(false);
    } catch (err) {
      setError('Error fetching videos. Please try again later.');
      setLoading(false);
      console.error('Error fetching videos:', err);
    }
  }, [isAuthenticated, keycloak, fetchVideosAPI]);

  // Procesar video
  const processVideoRequest = useCallback(async (videoId, controller) => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (isAuthenticated && keycloak.token) {
      headers['Authorization'] = `Bearer ${keycloak.token}`;
    }

    const response = await fetch(downloadVideosAPI + "/" + videoId, {
      method: 'POST',
      headers,
      signal: controller.signal,
    });

    if (response.status === 422) {
      throw new Error('TRANSCRIPTION_MISSING');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }, [isAuthenticated, keycloak, downloadVideosAPI]);


  const handleProcessVideo = async (videoId) => {
    if (!isAuthenticated) {
      setProcessingError('You must log in to process videos.');

      const shouldLogin = window.confirm('You need to log in to process videos. Do you want to log in now?');
      if (shouldLogin) {
        keycloak.login();
      }
      return;
    }

    const video = videos.find(v => v.id === videoId);
    if (video && video.hasTranscription === false) {
      setProcessingError('Debes subir la transcripción para poder procesar este video.');
      return;
    }

    try {
      setProcessingError(null);


      setVideos(prevVideos =>
        prevVideos.map(v =>
          v.id === videoId ? { ...v, isProcessing: true } : v
        )
      );

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      const data = await processVideoRequest(videoId, controller);
      console.log('Respuesta del servidor:', data);

      clearTimeout(timeoutId);


      setVideos(prevVideos =>
        prevVideos.map(v =>
          v.id === videoId ? { ...v, processed: true, isProcessing: false } : v
        )
      );

      alert('Video processed successfully!');
    } catch (err) {
      console.error('Error processing video:', err);

      setVideos(prevVideos =>
        prevVideos.map(v =>
          v.id === videoId ? { ...v, isProcessing: false } : v
        )
      );

      if (err.message === 'TRANSCRIPTION_MISSING') {
        setProcessingError('Debes subir la transcripción para poder procesar este video.');
      } else {
        setProcessingError('Error al procesar el video. Por favor, intenta de nuevo.');
      }
    }
  };

  const handleProcessAllVideos = async (videoId) => {
    if (!isAuthenticated) return;

    setVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === videoId ? { ...video, isProcessing: true } : video
      )
    );

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const data = await processVideoRequest(videoId, controller);
      clearTimeout(timeoutId);
      setVideos(prevVideos =>
        prevVideos.map(video =>
          video.id === videoId ? { ...video, processed: true, isProcessing: false } : video
        )
      );
      return data;
    } catch (err) {
      clearTimeout(timeoutId);
      setVideos(prevVideos =>
        prevVideos.map(video =>
          video.id === videoId ? { ...video, isProcessing: false } : video
        )
      );
      throw err;
    }
  };

  // Solo cargar los videos si el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      fetchVideos(); // Cargar los videos cuando esté autenticado
    }
  }, [isAuthenticated, fetchVideos]);

  // Extracted ternary operations for better readability

  const authMessage = !isAuthenticated ? (
    <div className="auth-message">
      <p>You must log in to process a video.</p>
      <button className="login-button-large" onClick={() => keycloak.login()}>Log In</button>
    </div>
  ) : null;

  const loadingMessage = loading ? (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading videos...</p>
    </div>
  ) : null;

  const errorMessage = error ? (
    <div className="error-message">{error}</div>
  ) : null;

  const videoList = !loading && !error ? (
    <VideoList videos={videos} onProcessVideo={handleProcessVideo} onProcessAllVideos={handleProcessAllVideos} />
  ) : null;

  const processingErrorMessage = processingError ? (
    <div className="error-message">{processingError}</div>
  ) : null;

  const userInfo = isAuthenticated ? (
    <small>Usuario: {keycloak.tokenParsed?.preferred_username}</small>
  ) : null;

  return (
    <div className="App">
      <header className="App-header">
        <h1>Video Processing App</h1>
        <div className="auth-controls">
          {isAuthenticated ? (
            <span className="user-info">
              Hi, {keycloak.tokenParsed?.preferred_username || 'Usuario'}
            </span>
          ) : (
            <button className="login-button" onClick={() => keycloak.login()}>Log In</button>
          )}
        </div>
      </header>
      <main>
        {authMessage}
        {loadingMessage}
        {errorMessage}
        {videoList}
        {processingErrorMessage}
      </main>

      <footer className="app-footer">
        <div className="app-info">
          <small>Mode: Standalone</small>
          {userInfo}
        </div>
      </footer>
    </div>
  );
}

// Add prop types for validation
App.propTypes = {
  keycloak: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
};

export default App;